import { useEffect, useState } from 'react';
import swal from 'sweetalert';
import { Link } from 'react-router-dom';
import {
  addInvoiceReceipt,
  adminRecordSubscriptionPayment,
  approvePumpDraftInvoice,
  fetchPumpInvoices,
} from '../adminApi';
import { formatInrAmount } from '../../../../utils/reportFormat';

const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'upi', label: 'UPI' },
  { value: 'credit', label: 'Credit' },
];

function todayDateString() {
  return new Date().toISOString().slice(0, 10);
}

const AdminSubscriptionBillingModal = ({ row, onClose, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_mode: 'cash',
    reference_no: '',
    paid_by: '',
    receipt_date: todayDateString(),
  });

  useEffect(() => {
    if (!row?.pump_id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    fetchPumpInvoices(row.pump_id)
      .then((res) => {
        const invoices = (res.invoices || []).filter(
          (item) => item.subscription_id === row.id || item.invoice_source === 'subscription'
        );
        const match = invoices.find((item) => item.subscription_id === row.id)
          || invoices[0]
          || null;
        setInvoice(match);
        if (match?.amount_pending > 0) {
          setPaymentForm((prev) => ({
            ...prev,
            amount: String(match.amount_pending),
          }));
        }
      })
      .catch((error) => swal('Billing', error.message, 'error'))
      .finally(() => setLoading(false));
  }, [row]);

  async function handleApprove() {
    if (!invoice?.id) return;
    try {
      const res = await approvePumpDraftInvoice(row.pump_id, invoice.id);
      setInvoice(res.invoice || invoice);
      swal('Approved', res.message || 'Invoice approved', 'success');
      onSaved?.();
    } catch (error) {
      swal('Billing', error.message, 'error');
    }
  }

  async function handleRecordPayment() {
    const amount = Number(paymentForm.amount);
    if (!amount || amount <= 0) {
      swal('Amount required', 'Enter a payment amount greater than zero', 'warning');
      return;
    }

    try {
      let res;
      if (row.id) {
        res = await adminRecordSubscriptionPayment(row.id, {
          amount,
          payment_mode: paymentForm.payment_mode,
          reference_no: paymentForm.reference_no || undefined,
          paid_by: paymentForm.paid_by || undefined,
          receipt_date: paymentForm.receipt_date,
          auto_approve_invoice: invoice?.is_draft ?? false,
        });
      } else if (invoice?.id) {
        if (invoice.is_draft) {
          await approvePumpDraftInvoice(row.pump_id, invoice.id);
        }
        res = await addInvoiceReceipt(row.pump_id, invoice.id, {
          amount,
          payment_mode: paymentForm.payment_mode,
          reference_no: paymentForm.reference_no || undefined,
          paid_by: paymentForm.paid_by || undefined,
          receipt_date: paymentForm.receipt_date,
        });
      }

      swal('Saved', res?.message || 'Payment recorded', 'success');
      setInvoice(res?.invoice || invoice);
      onSaved?.();
    } catch (error) {
      swal('Billing', error.message, 'error');
    }
  }

  return (
    <>
      <div className="admin-sub-modal-backdrop" onClick={onClose} />
      <div className="admin-sub-modal admin-sub-billing-modal" role="dialog" aria-modal="true">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="mb-0">Subscription billing</h5>
            <p className="small text-muted mb-0">{row.pump_name}</p>
          </div>
          <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
        </div>

        {loading ? (
          <p className="text-muted">Loading invoice details…</p>
        ) : (
          <>
            <div className="admin-sub-billing-summary mb-3">
              <div><strong>Plan:</strong> {row.plan_name || '—'}</div>
              <div><strong>Period:</strong> {row.subscription_start_date} → {row.subscription_end_date}</div>
              {row.billing && (
                <div className="mt-2">
                  <span className="badge bg-secondary me-2">{row.billing.workflow}</span>
                  Pending: {formatInrAmount(row.billing.amount_pending)}
                </div>
              )}
            </div>

            {!invoice ? (
              <p className="text-muted">
                No invoice linked yet. Add a subscription with invoice generation enabled, or create one from the pump hub.
              </p>
            ) : (
              <>
                <div className="border rounded p-3 mb-3 bg-light">
                  <div className="d-flex justify-content-between flex-wrap gap-2">
                    <div>
                      <strong>Invoice #{invoice.id}</strong>
                      <div className="small text-muted">{invoice.workflow || (invoice.is_draft ? 'draft' : 'approved')}</div>
                    </div>
                    <div className="text-end">
                      <div>Total {formatInrAmount(invoice.total)}</div>
                      <div className="small">Received {formatInrAmount(invoice.amount_received)} · Pending {formatInrAmount(invoice.amount_pending)}</div>
                    </div>
                  </div>
                  {invoice.is_draft && (
                    <button type="button" className="btn btn-sm btn-outline-primary mt-2" onClick={handleApprove}>
                      Approve invoice (client can download)
                    </button>
                  )}
                </div>

                <h6 className="mb-2">Record payment</h6>
                <div className="row g-2 mb-3">
                  <div className="col-md-4">
                    <label className="form-label small">Amount (₹)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="form-control"
                      value={paymentForm.amount}
                      onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small">Mode</label>
                    <select
                      className="form-select"
                      value={paymentForm.payment_mode}
                      onChange={(e) => setPaymentForm({ ...paymentForm, payment_mode: e.target.value })}
                    >
                      {PAYMENT_MODES.map((mode) => (
                        <option key={mode.value} value={mode.value}>{mode.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small">Receipt date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={paymentForm.receipt_date}
                      onChange={(e) => setPaymentForm({ ...paymentForm, receipt_date: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">Reference no.</label>
                    <input
                      type="text"
                      className="form-control"
                      value={paymentForm.reference_no}
                      onChange={(e) => setPaymentForm({ ...paymentForm, reference_no: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">Paid by</label>
                    <input
                      type="text"
                      className="form-control"
                      value={paymentForm.paid_by}
                      onChange={(e) => setPaymentForm({ ...paymentForm, paid_by: e.target.value })}
                    />
                  </div>
                </div>
                <button type="button" className="btn btn-success" onClick={handleRecordPayment}>
                  Save payment / receipt
                </button>
              </>
            )}

            <div className="mt-3 pt-3 border-top">
              <Link
                to={`/admin-pump/${row.pump_id}?module=invoice&section=invoices`}
                className="btn btn-sm btn-outline-secondary"
                onClick={onClose}
              >
                Open full invoice manager →
              </Link>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default AdminSubscriptionBillingModal;
